import router from "@/router";
import userUserStore from "@/store/modules/user";
import { GET_TOKEN, REMOVE_TOKEN } from "@/utils/token";
import { UserControllerService } from "../../generated";
import setting from "@/setting";
import ACCESS_ENUM from "@/access/accessEnum";
import { getCurrent } from "@/api/user/user";

/**
 * 路由守卫白名单（无需登录即可访问的页面）
 */
const whiteList = ["/", "/login", "/register"];

/**
 * 判断用户是否有访问权限
 * @param to 目标路由
 * @returns boolean 是否有权限
 */
const hasAccess = (to: any): boolean => {
  const metaAccess = to.meta?.access as string;

  // 如果没有配置 access 属性，默认允许访问
  if (!metaAccess) {
    return true;
  }
  if (metaAccess === ACCESS_ENUM.NOT_LOGIN) {
    // 标记为无需登录，直接放行
    return true;
  }

  if (metaAccess === ACCESS_ENUM.USER) {
    // 需要用户登录
    const token = GET_TOKEN();
    if (!token) return false;

    // 有 token 但需要验证用户角色
    const userStore = userUserStore();
    const userRole = userStore.loginUser?.userRole;

    // 如果 userRole 不为 notLogin 【admin或者user】 都可以访问
    if (userRole != ACCESS_ENUM.NOT_LOGIN) {
      return true;
    }

    return false;
  }

  if (metaAccess === ACCESS_ENUM.ADMIN) {
    // 需要管理员权限
    const token = GET_TOKEN();
    if (!token) return false;

    const userStore = userUserStore();
    const userRole = userStore.loginUser?.userRole;

    // 检查是否为 admin 角色
    if (userRole !== ACCESS_ENUM.ADMIN) {
      return false;
    }

    return true;
  }

  // 未知或无效的配置，默认拒绝访问
  return false;
};

/**
 * 判断用户是否已登录
 * @returns boolean
 */
const isLogin = (): boolean => {
  const token = GET_TOKEN();
  return !!token;
};

/**
 * 判断用户是否为管理员
 * @returns boolean
 */
const isAdmin = (): boolean => {
  const userStore = userUserStore();
  const userRole = userStore.loginUser?.userRole;
  return userRole === "admin";
};

/**
 * 判断用户是否为普通用户（已登录且非管理员）
 * @returns boolean
 */
const isNormalUser = (): boolean => {
  return isLogin() && !isAdmin();
};

/**
 * 判断用户是否为游客（未登录）
 * @returns boolean
 */
const isGuest = (): boolean => {
  return !isLogin();
};

/**
 * 判断是否需要获取用户信息
 * @param userRole 用户角色
 * @returns boolean
 */
const needFetchUserInfo = (userRole: string | undefined): boolean => {
  // 游客不需要获取，已登录用户如果 userRole 为 "user" 也不需要
  if (userRole === ACCESS_ENUM.NOT_LOGIN) {
    return false;
  }
  //用户已经登录，userRole为user跟Admin
  return true;
};

router.beforeEach(async (to, from, next) => {
  document.title = `${setting.title} - ${to.meta.title || ""}`;
  const userStore = userUserStore();
  const token = GET_TOKEN();

  // 在白名单中直接放行
  if (whiteList.includes(to.path)) {
    next();
    return;
  }

  // 检查访问权限
  if (!hasAccess(to)) {
    console.log(`[路由守卫] 拒绝访问: ${to.path}`);
    console.log(
      `[路由守卫] 权限不足: userRole=${
        userStore.loginUser?.userRole
      }, hasToken=${!!token}`
    );
    // 如果已经在登录页，避免无限循环
    if (to.path === "/login") {
      next();
      return;
    }
    next({
      path: "/login",
      query: { redirect: to.fullPath },
    });
    return;
  }

  // 无需登录的页面，直接放行
  next();
  //User或者Admin
  const shouldFetchUser = needFetchUserInfo(userStore.loginUser?.userRole);
  // 如果有 token 但用户信息不完整（userId 或 userRole 为空）
  // 或者是普通用户但信息不完整
  if (token && shouldFetchUser) {
    // 防止并发请求
    //只请求一次
    if (userStore.isFetchingUser) {
      next();
      return;
    }
    //开始获取用户信息
    userStore.isFetchingUser = true;

    try {
      const res = await getCurrent();

      // console.log("[路由守卫] 获取用户信息:", res);

      if (res.code === 0) {
        // 设置完整用户信息
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await userStore.setFullUserInfo(res.data);
        userStore.isFetchingUser = false;
        next();
      } else if (res.code === 40100) {
        // token 确实失效，清除状态并跳登录页
        userStore.isFetchingUser = false;
        userStore.token = "";
        REMOVE_TOKEN();
        // console.log("[路由守卫] Token 失效，重定向到登录页");
        next(`/login?redirect=${to.fullPath}`);
        return;
      } else {
        // 其他业务错误，不清除 token，允许访问
        // console.error("[路由守卫] 获取用户信息失败:", res.message);
        userStore.isFetchingUser = false;
        next();
        return;
      }
    } catch (error) {
      // console.error("[路由守卫] 获取用户信息时发生错误:", error);
      userStore.isFetchingUser = false;
      next();
    }
  } else if (token) {
    // 有 token 但不需要获取用户信息的情况
    // console.log("[路由守卫] 有 Token，跳过用户信息获取");
    userStore.isFetchingUser = false;
    next();
  } else {
    // 没有 token，跳转登录页
    // console.log("[路由卫] 无 Token，跳转到登录页");
    next(`/login?redirect=${to.fullPath}`);
  }
});
